import { useReactiveVar } from '@apollo/client'
import { IconButton, Menu, MenuItem, Box } from '@material-ui/core'
import React, { useState } from 'react'
import { MoreHorizontal } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { projectVar } from '../../../apollo/reactive-vars'
import { useAuthorization } from '../../../authorization/use-authorization'
import { useBuildingEnergyModels } from '../../../building-energy-model/use-building-energy-models'
import {
  BuildingEnergyModelFragment,
  UpdateBuildingEnergyModelInput,
} from '../../../generated/graphql'
import { useAlert } from '../../alert/AlertProvider'
import { LoadingDialog } from '../../common/LoadingDialog'
import { BuildingEnergyModelsDialog } from '../dialog/BuildingEnergyModelsDialog'
import { BaseDataType, FormValues } from '../dialog/form-values'
import { CalibrationDrawer } from '../calibration/CalibrationDrawer'

type Props = {
  buildingEnergyModel: BuildingEnergyModelFragment
  onClose: () => void
}

export function BuildingEnergyModelOptions({
  buildingEnergyModel,
  onClose,
}: Props): React.ReactElement {
  const project = useReactiveVar(projectVar)
  const { deleteBuildingEnergyModel, isUpdateLoading } =
    useBuildingEnergyModels()
  const { alert } = useAlert()
  const { t } = useTranslation()
  const [isEditing, setIsEditing] = useState(false)
  const [isCalibrationDrawerOpen, setIsCalibrationDrawerOpen] = useState(false)

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)

  const canEditProject = useAuthorization().canEditProject(project)

  const { updateBuildingEnergyModel } = useBuildingEnergyModels()

  if (!project) {
    return <div />
  }

  const onEditSubmit = async ({ name, type, area }: FormValues) => {
    setIsEditing(false)
    const input: Partial<UpdateBuildingEnergyModelInput> = {
      name,
      type,
      area: Number(area),
    }

    await updateBuildingEnergyModel(buildingEnergyModel.id, input)
  }

  const onEditStart = () => {
    setMenuAnchorEl(null)
    setIsEditing(true)
  }

  const onDelete = () => {
    setMenuAnchorEl(null)

    alert(
      t('buildingEnergyModels.deleteDialog.title'),
      t('buildingEnergyModels.deleteDialog.message'),
      [
        { title: t('common.cancel') },
        {
          title: t('buildingEnergyModels.deleteDialog.confirm'),
          color: 'secondary',
          onPress: async () => {
            await deleteBuildingEnergyModel(project, buildingEnergyModel.id)
            onClose()
          },
        },
      ],
    )
  }

  const onCalibrationDrawerOpen = () => {
    setMenuAnchorEl(null)
    setIsCalibrationDrawerOpen(true)
  }

  const onCalibrationDrawerClose = () => {
    setIsCalibrationDrawerOpen(false)
  }

  const enableCalibration =
    buildingEnergyModel.energySystems.length > 0 &&
    buildingEnergyModel.schedules.length > 0 &&
    canEditProject

  return (
    <Box>
      <IconButton onClick={(event) => setMenuAnchorEl(event.currentTarget)}>
        <MoreHorizontal />
      </IconButton>
      <Menu
        anchorEl={menuAnchorEl}
        keepMounted
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
      >
        <MenuItem
          onClick={onCalibrationDrawerOpen}
          disabled={!enableCalibration}
        >
          {t('buildingEnergyModels.calibrate')}
        </MenuItem>
        <MenuItem onClick={onEditStart} disabled={!canEditProject}>
          {t('buildingEnergyModels.edit')}
        </MenuItem>
        <MenuItem onClick={onDelete} disabled={!canEditProject}>
          {t('buildingEnergyModels.delete')}
        </MenuItem>
      </Menu>

      <BuildingEnergyModelsDialog
        isOpen={isEditing}
        defaultValues={{
          name: buildingEnergyModel.name,
          area: buildingEnergyModel.area.toString(),
          type: buildingEnergyModel.type,
          baseDataType: BaseDataType.MeasuredData,
          demandHourSeriesIds: [],
        }}
        onClose={() => setIsEditing(false)}
        onSubmit={onEditSubmit}
      />

      <CalibrationDrawer
        isOpen={isCalibrationDrawerOpen}
        onClose={onCalibrationDrawerClose}
        energyModel={buildingEnergyModel}
      />

      <LoadingDialog isLoading={isUpdateLoading} />
    </Box>
  )
}
